
import { createContext, ReactNode, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { apiV2 } from '../services/api';
import LocalStorageService from '../services/storage';

const localStorageService = LocalStorageService();

interface Trip {
    id_trip: string;
    begin_date?: string;
    end_date?: string | null;
    oil_consumption_engine_1: number;
    oil_consumption_engine_2: number;
    oil_consumption_engine_3: number;
    production: number;
    trips_name: number;
    notes: string;
    fk_boats_id_boat: string;
    fk_fishingtypes_id_fishingtype: string;
    boatName: string;
    fishingTypeName: string;
    harvest: string;
    estimated_weight?: number;
    compensation_species?: CompensationSpecies;
    trip_formation?: TripFormation;
    employees?: TripEmployeeInput[];
    isactive?: boolean;
    actions?: {
        canEditCrew?: boolean;
        crewEditBlockedReason?: string | null;
        canDelete?: boolean;
        deleteBlockedReason?: string | null;
    };
    // boats_name: string;
    // fishingtypes_name: string;
}
type TripInput = Omit<Trip, 'id_trip'>

type CompensationSpecies = 'GO' | 'PIRAMUTABA' | 'CAMARAO';
type TripFormation = 'INDIVIDUAL' | 'PARELHA' | 'TRILHEIRA';
type CrewRole = 'MESTRE' | 'MOTORISTA' | 'COZINHEIRO' | 'GELADOR' | 'PESCADOR';

type TripEmployeeInput = string | {
    employee?: string;
    id_employee?: string;
    crew_role?: CrewRole;
}

interface TripPayload {
    harvest: number;
    begin_date: string;
    end_date: string | null;
    oil_consumption_engine_1: number;
    oil_consumption_engine_2: number;
    oil_consumption_engine_3: number;
    production: number;
    trips_name: number;
    notes: string;
    fk_boats_id_boat: string;
    fk_fishingtypes_id_fishingtype: string;
    estimated_weight?: number;
    compensation_species?: CompensationSpecies;
    trip_formation?: TripFormation;
    employees: Array<{ employee: string; crew_role?: CrewRole }>;
}

interface TripsProvidersProps {
    children: ReactNode;
}

interface OverviewRequest {
    page?: number;
    pageSize?: number;
    harvest?: number | string;
    tripNumber?: number | string;
    boatId?: string;
    fishTypeId?: string;
    productId?: string;
    employeeId?: string;
    costCategoryId?: string;
    providerId?: string;
}

interface TripsPaginatedRequest {
    page?: number;
    pageSize?: number;
    harvest?: number | string;
    status?: 'OPEN' | 'CLOSED' | string;
    boatId?: string;
    fishTypeId?: string;
    beginDateFrom?: string;
    beginDateTo?: string;
    tripNumber?: number | string;
}

interface ResponseProps {
    success: boolean;
    message: string;
    data?: any;
}

interface ResponsePropsWithData {
    success: boolean;
    message: string;
    data: any;
}

interface ProductionAdvanceAllocationInput {
    advance_id: string | number;
    amount: number;
}

interface RemunerationBalanceAllocationInput {
    origin_id: string | number;
    amount: number;
}

interface RemunerationPaymentOrderInput {
    order_id: string;
    payment_type_id: string;
}

interface TripsContextData {
    createTrip: (trip: TripInput) => Promise<ResponseProps>;
    updateTrip: (trip: Trip) => Promise<ResponseProps>;
    updateOnDelete: (id: string | number) => Promise<ResponseProps>;
    getTripById: (id: string | number) => Promise<ResponsePropsWithData>;
    getTripRemunerationPreview: (id: string | number) => Promise<ResponsePropsWithData>;
    generateEstimatedRemuneration: (id: string | number, allocations?: ProductionAdvanceAllocationInput[], remunerationBalanceAllocations?: RemunerationBalanceAllocationInput[]) => Promise<ResponsePropsWithData>;
    getEstimatedRemuneration: (id: string | number) => Promise<ResponsePropsWithData>;
    deleteEstimatedRemuneration: (id: string | number) => Promise<ResponsePropsWithData>;
    getTripBonusPreview: (id: string | number) => Promise<ResponsePropsWithData>;
    generateTripBonus: (id: string | number) => Promise<ResponsePropsWithData>;
    getTripBonus: (id: string | number) => Promise<ResponsePropsWithData>;
    deleteTripBonus: (id: string | number) => Promise<ResponsePropsWithData>;
    getTripRemunerationPaymentOrders: (id: string | number) => Promise<ResponsePropsWithData>;
    payTripRemunerationOrders: (id: string | number, paymentDate: string, orders: RemunerationPaymentOrderInput[]) => Promise<ResponsePropsWithData>;
    reverseTripRemunerationOrdersPayment: (id: string | number, orderIds: string[]) => Promise<ResponsePropsWithData>;
    getTripOrders: (trip: string) => Promise<ResponsePropsWithData>;
    getFilteredTrips: (request: TripsPaginatedRequest) => Promise<ResponsePropsWithData>;
    getTripsByBoats: (request: OverviewRequest) => Promise<ResponsePropsWithData>;
    getTripsOverview: (request: OverviewRequest) => Promise<ResponsePropsWithData>;

}

interface UseTripsOptions {
    loadTrips?: boolean;
}

interface UseTripsData extends TripsContextData {
    trips: Trip[];
}

const TripsContext = createContext<TripsContextData>(
    {} as TripsContextData
);

const tripsQueryKey = ['trips'];
const emptyTrips: Trip[] = [];

function hasToken() {
  return Boolean(localStorageService.getIdToken());
}

function isEmptyFilter(value: unknown) {
  return value === undefined || value === null || value === '' || value === "''";
}

function removeEmptyFilters(request: TripsPaginatedRequest) {
  return Object.entries(request).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (!isEmptyFilter(value)) acc[key] = value;
    return acc;
  }, {});
}

function isTechnicalMessage(message: unknown) {
  if (typeof message !== 'string') return true;

  return /^(cannot\s+(get|post|put|patch|delete)|axioserror|error:|network error|internal server error|failed with status code)/i.test(message.trim()) ||
    /<\/?[a-z][\s\S]*>/i.test(message) ||
    /\/api\/v\d/i.test(message);
}

function getApiMessage(response: any) {
  return response?.data?.message || response?.message || response?.data?.errors?.message || response?.data?.errors?.toString?.();
}

export function translateKnownTripApiMessage(
  message: unknown,
  validationMessage = 'Não foi possível validar os filtros enviados. Revise as informações e tente novamente.'
) {
  if (typeof message !== 'string') return undefined;

  const normalizedMessage = message.trim();

  if (/^validation failed\.?$/i.test(normalizedMessage)) {
    return validationMessage;
  }

  if (/beginDateTo must be on or after beginDateFrom/i.test(normalizedMessage)) {
    return 'A data final de saída deve ser igual ou posterior à data inicial.';
  }

  if (/expected a valid date in yyyy-mm-dd format|invalid date/i.test(normalizedMessage)) {
    return 'Informe as datas no formato AAAA-MM-DD.';
  }

  if (/(page|pagesize).*(greater than|less than|min|max|integer|number)|number must be (greater than or equal to|less than or equal to)/i.test(normalizedMessage)) {
    return 'Informe uma página e uma quantidade por página válidas.';
  }

  if (/invalid enum value|expected.*OPEN.*CLOSED|status/i.test(normalizedMessage)) {
    return 'Informe um status de viagem válido.';
  }

  if (/invalid uuid|expected uuid|boatId|fishTypeId/i.test(normalizedMessage)) {
    return 'Informe barco e espécie alvo válidos.';
  }

  if (/tripNumber/i.test(normalizedMessage)) {
    return 'Informe um número de viagem válido.';
  }

  if (/estimated.?weight/i.test(normalizedMessage)) {
    return 'Informe um peso estimado válido.';
  }

  if (/compensation.?species/i.test(normalizedMessage)) {
    return 'Informe uma espécie para remuneração válida.';
  }

  if (/trip.?formation/i.test(normalizedMessage)) {
    return 'Informe uma formação de viagem válida.';
  }

  if (/crew.?role/i.test(normalizedMessage)) {
    return 'Informe uma função de tripulação válida.';
  }

  if (/^trip not found\.?$/i.test(normalizedMessage)) {
    return 'Viagem não encontrada ou não está mais disponível.';
  }

  if (/trip must belong to a round before estimating remuneration/i.test(normalizedMessage)) {
    return 'A viagem precisa pertencer a uma rodada antes de gerar a estimativa de remuneração.';
  }

  if (/trip must have a begin date before (estimating|generating) remuneration/i.test(normalizedMessage)) {
    return 'A viagem precisa ter data de saída antes de gerar a estimativa de remuneração.';
  }

  if (/estimated remuneration has already been generated for this trip/i.test(normalizedMessage)) {
    return 'A antecipação estimada desta viagem já foi gerada.';
  }

  if (/estimated remuneration has not been generated for this trip/i.test(normalizedMessage)) {
    return 'Não há pedidos de remuneração estimada gerados para esta viagem.';
  }

  if (/generated remuneration orders cannot be deleted because one or more orders have been executed/i.test(normalizedMessage)) {
    return 'Os pedidos gerados não podem ser removidos porque um ou mais já foram executados.';
  }

  if (/trip crew cannot be changed after estimated remuneration has been generated\.?( delete the generation first\.)?/i.test(normalizedMessage)) {
    return 'A tripulação não pode ser alterada porque há pedidos estimados de remuneração gerados para esta viagem. Remova-os primeiro.';
  }

  if (/active cost category produção was not found|active cost category PRODUÇÃO was not found/i.test(normalizedMessage)) {
    return 'A categoria de custo PRODUÇÃO precisa estar ativa para gerar os pedidos.';
  }

  if (/active payment type dinheiro was not found/i.test(normalizedMessage)) {
    return 'O tipo de pagamento Dinheiro precisa estar ativo para gerar os pedidos.';
  }

  if (/active cost center named .* was not found/i.test(normalizedMessage)) {
    return 'O centro de custo do barco precisa estar ativo para gerar os pedidos.';
  }

  if (/active cost category comissões .* was not found/i.test(normalizedMessage)) {
    return 'A categoria de custo de comissões precisa estar ativa para gerar os pedidos.';
  }

  if (/trip must belong to a round before calculating bonus/i.test(normalizedMessage)) return 'A viagem precisa pertencer a uma rodada antes de calcular a gratificação.';
  if (/trip must be closed before (calculating|generating) bonus/i.test(normalizedMessage)) return 'A viagem precisa estar encerrada antes de calcular ou gerar a gratificação.';
  if (/trip must have a begin date before generating bonus/i.test(normalizedMessage)) return 'A viagem precisa ter data de saída antes de gerar a gratificação.';
  if (/trip production must be informed before calculating bonus/i.test(normalizedMessage)) return 'Informe a produção da viagem antes de calcular a gratificação.';
  if (/bonus has already been generated for this trip/i.test(normalizedMessage)) return 'A gratificação desta viagem já possui pedidos gerados.';
  if (/bonus generation was not found/i.test(normalizedMessage)) return 'Não há pedidos de gratificação gerados para esta viagem.';
  if (/generated bonus orders cannot be deleted after execution/i.test(normalizedMessage)) return 'Os pedidos de gratificação não podem ser removidos porque um ou mais já foram executados.';
  if (/active cost category gratificação was not found/i.test(normalizedMessage)) return 'A categoria de custo GRATIFICAÇÃO precisa estar ativa para gerar os pedidos.';
  if (/a bonus employee is inactive or belongs to another company/i.test(normalizedMessage)) return 'Um colaborador da gratificação não está ativo ou não pertence à empresa.';
  if (/^user not found\.?$/i.test(normalizedMessage)) return 'O usuário responsável pela operação não foi encontrado. Entre novamente no sistema.';
  if (/^round must contain at least one trip\.?$/i.test(normalizedMessage)) return 'A rodada deve possuir pelo menos uma viagem.';
  if (/^every round trip needs a begin date\.?$/i.test(normalizedMessage)) return 'Informe a data de início de todas as viagens da rodada.';
  if (/^every round trip must be closed before calculating actual remuneration\.?$/i.test(normalizedMessage)) return 'Todas as viagens da rodada devem estar encerradas antes de calcular a apuração real.';
  if (/^commission rule .+ has no employee\.?$/i.test(normalizedMessage)) return 'Uma regra de comissão está sem colaborador. Revise as regras de remuneração cadastradas.';
  if (/^base type .+ is not supported yet\.?$/i.test(normalizedMessage)) return 'Uma regra utiliza uma base de cálculo não suportada. Revise as regras de remuneração cadastradas.';
  if (/^calculation type .+ is not supported\.?$/i.test(normalizedMessage)) return 'Uma regra utiliza um tipo de cálculo não suportado. Revise as regras de remuneração cadastradas.';
  if (/an order can only be paid once per request/i.test(normalizedMessage)) return 'Um pedido só pode ser pago uma vez por confirmação.';
  if (/an order can only be reverted once per request/i.test(normalizedMessage)) return 'O pagamento de um pedido só pode ser desfeito uma vez por confirmação.';
  if (/was not generated by the remuneration feature for this trip/i.test(normalizedMessage)) return 'Um ou mais pedidos não pertencem à remuneração desta viagem.';
  if (/is inactive or has already been executed/i.test(normalizedMessage)) return 'Um ou mais pedidos estão inativos ou já foram pagos.';
  if (/is inactive or is not executed/i.test(normalizedMessage)) return 'Um ou mais pedidos estão inativos ou não estão pagos.';
  if (/one or more payment types are inactive or do not belong to this company/i.test(normalizedMessage)) return 'Um ou mais tipos de pagamento não estão disponíveis.';
  if (/^order not found\.?$/i.test(normalizedMessage)) return 'Um dos pedidos selecionados não foi encontrado ou não está mais disponível.';
  if (/^order has already been executed\.?$/i.test(normalizedMessage)) return 'Um dos pedidos selecionados já foi pago.';
  if (/^order is not executed\.?$/i.test(normalizedMessage)) return 'Um dos pedidos selecionados ainda não foi pago.';
  if (/installment orders cannot be paid by payment import/i.test(normalizedMessage)) return 'Pedidos parcelados não podem ser pagos por esta ação.';
  if (/installment orders cannot be reverted by this operation/i.test(normalizedMessage)) return 'O pagamento de pedidos parcelados não pode ser desfeito por esta ação.';

  if (/a production advance can only be allocated once per trip generation/i.test(normalizedMessage)) {
    return 'Cada adiantamento pode ser abatido apenas uma vez nesta geração.';
  }

  if (/one or more production advances are unavailable, inactive, or not executed/i.test(normalizedMessage)) {
    return 'Um ou mais adiantamentos não estão disponíveis para abatimento.';
  }

  if (/(requested production advance allocation exceeds|a production advance allocation cannot exceed) its available balance/i.test(normalizedMessage)) {
    return 'O valor abatido não pode ser maior que o saldo disponível do adiantamento.';
  }

  if (/a production advance can only be allocated to a trip that begins after its payment date/i.test(normalizedMessage)) {
    return 'O adiantamento só pode ser abatido em uma viagem iniciada após a data do pagamento.';
  }

  if (/production advance allocation cannot exceed this employee estimated production for the trip/i.test(normalizedMessage)) {
    return 'O total abatido não pode ser maior que a previsão bruta de produção do colaborador.';
  }

  if (/the production advance employee is unavailable for this trip remuneration|production advance employee is inactive or belongs to another company/i.test(normalizedMessage)) {
    return 'O colaborador do adiantamento não está disponível para a remuneração desta viagem.';
  }

  if (/remuneration employee is inactive or belongs to another company/i.test(normalizedMessage)) {
    return 'Um colaborador da remuneração não está ativo ou não pertence à empresa.';
  }

  if (/a remuneration balance origin can only be allocated once per trip generation/i.test(normalizedMessage)) return 'Cada saldo pode ser utilizado apenas uma vez nesta geração.';
  if (/one or more remuneration balance origins were not found for this company/i.test(normalizedMessage)) return 'Um ou mais saldos selecionados não estão mais disponíveis.';
  if (/a remuneration balance allocation cannot exceed its available amount/i.test(normalizedMessage)) return 'O valor selecionado não pode ser maior que o saldo disponível.';
  if (/a remuneration balance can only be allocated to the same employee and remuneration category/i.test(normalizedMessage)) return 'O saldo só pode ser usado para o mesmo colaborador e tipo de remuneração.';
  if (/production advances and negative remuneration balances cannot exceed this employee remuneration for the trip/i.test(normalizedMessage)) return 'Adiantamentos e débitos de saldo não podem ultrapassar a previsão do colaborador.';
  if (/generated remuneration orders cannot be deleted because a positive remuneration balance order has been executed/i.test(normalizedMessage)) return 'Os pedidos gerados não podem ser removidos porque um crédito de saldo já foi executado.';

  if (/^you do not have permission to perform this action\.?$/i.test(normalizedMessage)) {
    return 'Você não tem permissão para realizar esta ação.';
  }

  if (/^unauthorized\.?$/i.test(normalizedMessage)) {
    return 'Sua sessão expirou. Faça login novamente.';
  }

  return undefined;
}

async function fetchTrips() {
  const response = await apiV2.get('trips');
  return Array.isArray(response?.data) ? response.data : [];
}

export function TripsProvider({ children }: TripsProvidersProps) {
  const queryClient = useQueryClient();

  function invalidateTrips() {
    queryClient.invalidateQueries({ queryKey: tripsQueryKey });
  }

  async function getTripOrders(trip: string) {
    const params = {
      fk_trips_id_trip: trip
    };
    const response = await api.get('/tripsOrders', { params: { ...api.defaults.params, ...params } });
    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar os pedidos relacionadas à viagem.",
      data: []
    };
  }

  async function getTripById(id: string | number) {
    const response: any = await apiV2.get(`/trips/${id}`);
    const success = response?.success !== false && response?.status >= 200 && response?.status < 300;

    if (success) {
      return {
        success: true,
        message: '',
        data: response.data
      };
    }

    const apiMessage = getApiMessage(response);
    const translatedMessage = translateKnownTripApiMessage(apiMessage, 'Não foi possível validar a viagem informada. Revise as informações e tente novamente.');

    return {
      success: false,
      message: translatedMessage || (isTechnicalMessage(apiMessage) ? 'Erro ao consultar os dados da viagem.' : apiMessage),
      data: null
    };
  }

  function toTripActionResponse(response: any, successMessage: string, errorMessage: string): ResponsePropsWithData {
    const success = response?.success !== false && response?.status >= 200 && response?.status < 300;
    const apiMessage = getApiMessage(response);
    const translatedMessage = translateKnownTripApiMessage(apiMessage, 'Não foi possível validar os dados da viagem. Revise as informações e tente novamente.');
    const hasDataProperty = response?.data && Object.prototype.hasOwnProperty.call(response.data, 'data');
    const payload = hasDataProperty ? response.data.data : response?.data;

    return {
      success,
      message: success ? successMessage : translatedMessage || (isTechnicalMessage(apiMessage) ? errorMessage : apiMessage),
      data: success ? payload : null
    };
  }

  async function getTripRemunerationPreview(id: string | number) {
    const response: any = await apiV2.get(`/trips/${id}/remuneration-preview`);
    return toTripActionResponse(response, 'Prévia de remuneração consultada com sucesso!', 'Não foi possível consultar a prévia de remuneração da viagem.');
  }

  async function generateEstimatedRemuneration(id: string | number, allocations: ProductionAdvanceAllocationInput[] = [], remunerationBalanceAllocations: RemunerationBalanceAllocationInput[] = []) {
    const response: any = await apiV2.post(`/trips/${id}/estimated-remuneration/generate`, {
      production_advance_allocations: allocations,
      remuneration_balance_allocations: remunerationBalanceAllocations
    });
    return toTripActionResponse(response, 'Pedidos da estimativa de produção gerados com sucesso!', 'Não foi possível gerar os pedidos da estimativa de produção.');
  }

  async function getEstimatedRemuneration(id: string | number) {
    const response: any = await apiV2.get(`/trips/${id}/estimated-remuneration`);
    return toTripActionResponse(response, 'Estimativa de produção consultada com sucesso!', 'Não foi possível consultar a estimativa de produção gerada.');
  }

  async function deleteEstimatedRemuneration(id: string | number) {
    const response: any = await apiV2.delete(`/trips/${id}/estimated-remuneration`);
    return toTripActionResponse(response, 'Pedidos da estimativa de produção removidos com sucesso!', 'Não foi possível remover os pedidos da estimativa de produção.');
  }

  async function getTripBonusPreview(id: string | number) {
    const response: any = await apiV2.get(`/trips/${id}/bonus-preview`);
    return toTripActionResponse(response, 'Prévia de gratificação consultada com sucesso!', 'Não foi possível consultar a prévia de gratificação da viagem.');
  }

  async function generateTripBonus(id: string | number) {
    const response: any = await apiV2.post(`/trips/${id}/bonus/generate`);
    return toTripActionResponse(response, 'Pedidos de gratificação gerados com sucesso!', 'Não foi possível gerar os pedidos de gratificação.');
  }

  async function getTripBonus(id: string | number) {
    const response: any = await apiV2.get(`/trips/${id}/bonus`);
    return toTripActionResponse(response, 'Gratificação gerada consultada com sucesso!', 'Não foi possível consultar os pedidos de gratificação gerados.');
  }

  async function deleteTripBonus(id: string | number) {
    const response: any = await apiV2.delete(`/trips/${id}/bonus`);
    return toTripActionResponse(response, 'Pedidos de gratificação removidos com sucesso!', 'Não foi possível remover os pedidos de gratificação.');
  }

  async function getTripRemunerationPaymentOrders(id: string | number) {
    const response: any = await apiV2.get(`/trips/${id}/remuneration-payment-orders`);
    return toTripActionResponse(response, 'Pedidos consultados com sucesso!', 'Não foi possível consultar os pedidos da viagem.');
  }

  async function payTripRemunerationOrders(id: string | number, paymentDate: string, orders: RemunerationPaymentOrderInput[]) {
    const response: any = await apiV2.post(`/trips/${id}/remuneration-payment-orders/pay`, {
      payment_date: paymentDate,
      orders
    });
    return toTripActionResponse(response, 'Pedidos pagos com sucesso!', 'Não foi possível pagar os pedidos selecionados.');
  }

  async function reverseTripRemunerationOrdersPayment(id: string | number, orderIds: string[]) {
    const response: any = await apiV2.post(`/trips/${id}/remuneration-payment-orders/reverse-payment`, {
      order_ids: orderIds
    });
    return toTripActionResponse(response, 'Pagamentos desfeitos com sucesso!', 'Não foi possível desfazer os pagamentos selecionados.');
  }

  async function getFilteredTrips(request: TripsPaginatedRequest) {
    const response: any = await apiV2.get('/trips/paginated', {
      params: removeEmptyFilters(request)
    });
    const success = response?.success !== false && response?.status >= 200 && response?.status < 300;

    if (success) {
      return {
        success: true,
        message: '',
        data: response.data
      };
    }

    const apiMessage = getApiMessage(response);
    const translatedMessage = translateKnownTripApiMessage(apiMessage);

    return {
      success: false,
      message: translatedMessage || (isTechnicalMessage(apiMessage) ? 'Erro ao consultar as viagens.' : apiMessage),
      data: []
    };
  }

  async function getTripsByBoats(request: OverviewRequest) {
    let params = {
      ...request
    };
    const response = await api.get('/tripsbyboat', { params: { ...api.defaults.params, ...params } });
    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar as viagens.",
      data: []
    };
  }

  async function getTripsOverview(request: OverviewRequest) {
    const response: any = await apiV2.get('/trip-overviews', {
      params: removeEmptyFilters(request)
    });
    const success = response?.success !== false && response?.status >= 200 && response?.status < 300;

    if (success) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar as viagens.",
      data: []
    };
  }

  async function updateOnDelete(id: string | number) {
    const response = await apiV2.delete(`/trips/${id}`);
    if (response.status === 200) {
      invalidateTrips();
      return {
        success: true,
        message: "Viagem removida com sucesso!"
      };
    }
    const apiMessage = getApiMessage(response);
    const translatedMessage = translateKnownTripApiMessage(
      apiMessage,
      'Não foi possível validar os dados da viagem. Revise as informações e tente novamente.'
    );

    return {
      success: false,
      message: translatedMessage || (isTechnicalMessage(apiMessage) ? 'Erro ao remover viagem.' : apiMessage)
    };
  }

  function normalizeTripEmployees(employees: TripEmployeeInput[] = []) {
    return employees
      .map((employee) => {
        if (typeof employee === 'string') {
          return { employee };
        }

        const employeeId = employee.employee || employee.id_employee;
        if (!employeeId) return undefined;

        return {
          employee: employeeId,
          ...(employee.crew_role ? { crew_role: employee.crew_role } : {})
        };
      })
      .filter(Boolean)
      .map((employee) => employee as { employee: string; crew_role?: CrewRole });
  }

  function normalizeTripPayload(trip: TripInput | Trip): TripPayload {
    return {
      harvest: Number(trip.harvest),
      begin_date: trip.begin_date || '',
      end_date: trip.end_date || null,
      oil_consumption_engine_1: Number(trip.oil_consumption_engine_1),
      oil_consumption_engine_2: Number(trip.oil_consumption_engine_2),
      oil_consumption_engine_3: Number(trip.oil_consumption_engine_3),
      production: Number(trip.production),
      trips_name: Number(trip.trips_name),
      notes: trip.notes || '',
      fk_boats_id_boat: trip.fk_boats_id_boat,
      fk_fishingtypes_id_fishingtype: trip.fk_fishingtypes_id_fishingtype,
      estimated_weight: trip.estimated_weight === undefined ? undefined : Number(trip.estimated_weight),
      compensation_species: trip.compensation_species,
      trip_formation: trip.trip_formation,
      employees: normalizeTripEmployees(trip.employees)
    };
  }

  async function createTrip(tripsInput: TripInput) {
    const response = await apiV2.post('/trips', normalizeTripPayload(tripsInput));
    if (response.status === 201) {
      invalidateTrips();
      return {
        success: true,
        message: "Viagem adicionada com sucesso!",
        data: response.data
      };
    }
    return {
      success: false,
      message: translateKnownTripApiMessage(getApiMessage(response), 'Não foi possível validar os dados da viagem. Revise as informações e tente novamente.') || 'Erro ao adicionar viagem.'
    };

  }

  async function updateTrip(trip: Trip) {
    const response = await apiV2.put(`/trips/${trip.id_trip}`, normalizeTripPayload(trip));
    if (response.status === 204) {
      invalidateTrips();
      return {
        success: true,
        message: "Viagem editada com sucesso!"
      };
    }
    return {
      success: false,
      message: translateKnownTripApiMessage(getApiMessage(response), 'Não foi possível validar os dados da viagem. Revise as informações e tente novamente.') || 'Erro ao editar viagem.'
    };
  }

  return (
    <TripsContext.Provider value={{ createTrip, updateTrip, updateOnDelete, getTripById, getTripRemunerationPreview, generateEstimatedRemuneration, getEstimatedRemuneration, deleteEstimatedRemuneration, getTripBonusPreview, generateTripBonus, getTripBonus, deleteTripBonus, getTripRemunerationPaymentOrders, payTripRemunerationOrders, reverseTripRemunerationOrdersPayment, getTripOrders, getFilteredTrips, getTripsByBoats, getTripsOverview }}>
      {children}
    </TripsContext.Provider>
  );
}

export function UseTrips(options: UseTripsOptions = {}): UseTripsData {
  const context = useContext(TripsContext);
  const { loadTrips = false } = options;

  const tripsQuery = useQuery({
    queryKey: tripsQueryKey,
    queryFn: fetchTrips,
    enabled: loadTrips && hasToken(),
    staleTime: 5 * 60 * 1000
  });

  return {
    ...context,
    trips: tripsQuery.data || emptyTrips
  };
}
